import React, { createContext, useContext, useState, useCallback } from 'react';
import { FieldValue } from '../../organisms';
import { useControlledValue } from '../FormField/useControlledValue';
import type { SelectContextType, SelectProviderProps } from './Select.types';
import { handleSelectionChange, hasValue } from './Select.utils';

// Initial context value for SelectContext
const selectContextInitialValue: SelectContextType = {
  selectedValue: '', // Initial selected value is empty string
  updateSelectedValue: () => { }, // function for updating selected value
  resetSelectedValue: () => { }, // function for resetting selected value
  registerOption: () => { }, // function for registering option labels
  getDisplayLabel: () => undefined, // function for getting display labels
};

// Create a context instance for Select component
const SelectContext = createContext<SelectContextType>(selectContextInitialValue);

/**
 * Custom hook to use the SelectContext values.
 * Throws an error if used outside of a SelectProvider.
 *
 * @throws Error if used outside of SelectProvider.
 * @returns Context values of type SelectContextType.
 */
export const useSelectContext = (): SelectContextType => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('useSelectContext must be used within a SelectProvider');
  }
  return context;
};

/**
 * Provider component for managing state and providing context to Select components.
 * Manages the selected value state and provides methods to update or reset it.
 *
 * @param props - Props for configuring the SelectProvider.
 * @returns JSX element that provides context to its children.
 */
export const SelectProvider = ({
  children,
  value = '',
  multiple = false,
  disabled,
  invalid,
  onValueChange,
  closeSelectOptions,
}: SelectProviderProps) => {

  const { value: selectedValue, onValueChange: handleValueChange } = useControlledValue<FieldValue>({ value, onValueChange, defaultValue: '' });

  // Store option labels in state to trigger re-renders when options register
  const [optionLabels, setOptionLabels] = useState<Map<string, React.ReactNode>>(new Map());

  /**
   * Registers an option's value-label pair.
   */
  const registerOption = useCallback((optionValue: FieldValue, label: React.ReactNode): void => {
    const key = String(optionValue);
    setOptionLabels(prev => {
      const newMap = new Map(prev);
      newMap.set(key, label);
      return newMap;
    });
  }, []);

  /**
   * Gets the display label for a value.
   */
  const getDisplayLabel = useCallback((optionValue: FieldValue): React.ReactNode | undefined => {
    if (Array.isArray(optionValue)) {
      if (optionValue.length === 0) {
        return undefined
      };

      if (optionValue.length >= 2) {
        return `${optionValue.length} items selected`;
      }

      const key = String(optionValue[0]);
      return optionLabels.get(String(key));
    }

    const key = String(optionValue);
    return optionLabels.get(key);
  }, [optionLabels]);

  /**
   * Updates the selected value based on the new value.
   * Handles multiple selections if enabled.
   *
   * @param newValue - The new value to be selected.
   */
  const updateSelectedValue = (newValue: string | number): void => {
    const currentSelection = handleSelectionChange(newValue, value, multiple);
    handleValueChange(currentSelection);
    if (!multiple && closeSelectOptions) {
      closeSelectOptions();
    }
  };

  /**
   * Resets the selected value to default or undefined.
   * Triggers the onValueChanged callback with default or undefined.
   */
  const resetSelectedValue = (): void => {
    const resetValue = value && hasValue(value) ? value : '';
    onValueChange?.(resetValue);
    if (!multiple && closeSelectOptions) {
      closeSelectOptions();
    }
  };

  return (
    <SelectContext.Provider
      value={{
        selectedValue: selectedValue ?? '',
        updateSelectedValue,
        resetSelectedValue,
        registerOption,
        getDisplayLabel,
        disabledField: disabled,
        invalidField: invalid,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};
